import { DateTime } from 'luxon';
import * as yup from 'yup';
import { z } from 'zod';

import type { Nullable } from '@clearverify/types';

const VALID_SEPARATORS = ['-', '/', '.'];

const SHORT_FORMAT_LENGTH = 5; // like MM/DD/YY
const LONG_FORMAT_LENGTH = 8; // like MM/DD/YY
const LONG_WITH_FULL_YEAR_FORMAT_LENGTH = 10; // like MM.DD.YYYY

const ONE_DIGIT_WITHOUT_YEAR_FORMAT_LENGTH = 3; // like M.D
const TWO_DIGIT_WITHOUT_YEAR_FORMAT_LENGTH = 4; // like MM.D or M.DD

const MM_DD_REGEX = /(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])/;

const getInvalidMessage = (
  reason = 'valid formats - MM-DD, MM-DD-YY, MM-DD-YYYY, M-D, MM-D, M-DD',
) => {
  // eslint-disable-next-line prefer-template
  return 'Invalid date' + (reason ? ` (${reason})` : '');
};

export const taxYearEndDateValidationSchema = yup
  .string()
  .trim()
  .test((value, context) => {
    if (!value || (value && value.length === 0)) return true;

    const commonContextError = context.createError({ message: getInvalidMessage() });

    let dateFormatArray: Nullable<string[]> = null;

    let separatorPosition = 0;

    const separator = VALID_SEPARATORS.find(separator => {
      const position = value.indexOf(separator);

      if (position > 0) {
        separatorPosition = position;

        return true;
      }

      return false;
    });

    if (value.length === SHORT_FORMAT_LENGTH) dateFormatArray = ['MM', 'dd'];
    if (value.length === LONG_FORMAT_LENGTH) dateFormatArray = ['MM', 'dd', 'yy'];
    if (value.length === LONG_WITH_FULL_YEAR_FORMAT_LENGTH) dateFormatArray = ['MM', 'dd', 'yyyy'];
    if (value.length === ONE_DIGIT_WITHOUT_YEAR_FORMAT_LENGTH) dateFormatArray = ['M', 'd'];
    if (value.length === TWO_DIGIT_WITHOUT_YEAR_FORMAT_LENGTH && separatorPosition === 1)
      dateFormatArray = ['M', 'dd'];
    if (value.length === TWO_DIGIT_WITHOUT_YEAR_FORMAT_LENGTH && separatorPosition === 2)
      dateFormatArray = ['MM', 'd'];

    if (dateFormatArray) {
      const dateFormat = dateFormatArray.join(separator);
      const date = DateTime.fromFormat(value, dateFormat);
      if (date.invalidExplanation) {
        return context.createError({ message: getInvalidMessage(date.invalidExplanation) });
      }
    } else {
      return commonContextError;
    }

    return true;
  });

export const taxYearEndDateSimpleValidationSchema = yup
  .string()
  .nullable()
  .test((value, context) => {
    if (!value || (value && value.length === 0)) return true;
    if (!MM_DD_REGEX.test(value)) {
      return context.createError({ message: getInvalidMessage('Must be in format MM/DD') });
    }
    return true;
  });

export const taxYearEndDateSimpleZodValidationSchema = z
  .string()
  .regex(MM_DD_REGEX, { message: 'Must be in format MM/DD' })
  .nullish()
  .refine(value => {
    if (!value || (value && value.length === 0)) return true;
    return true;
  });
